import {
  type TypeCurveForecast,
  type PdpDeclineForecast,
} from 'types/api-responses';
import { Fragment, type ReactNode } from 'react';
import { Tag } from '@carbon/react';
import { Date } from 'components/date';
import {
  TypeCurveVerificationStatusIndicator,
  typeCurveVerificationIsDecided,
} from 'components/type-curve-verification-status';
import { formatNumber } from 'utils/number';
import type { Forecast, VolumeForecastType } from './types';
import { displayValue, withAcronymsUpperCased } from './utils';
import styles from './volume-source-picker.module.scss';

/** One labelled piece of a details line, keyed for the list it is rendered from. */
interface DetailPart {
  key: string;
  content: ReactNode;
}

/**
 * Tags the forecast the row being edited is priced on — the one this evaluation uses — so it
 * stands out from the rest of the list, whichever well or area that list is for.
 *
 * @return {JSX.Element} The tag.
 */
export function InUseTag() {
  return (
    <Tag type="green" size="sm" className={styles.inUseTag}>
      Used in this Evaluation
    </Tag>
  );
}

/**
 * The leading "Version: N" part of a forecast's details. A decline's `isCurrent` marks its
 * well's newest decline — the default, not necessarily what the row is priced on — so it reads
 * "Latest"; the forecast in use gets {@link InUseTag} instead. A type curve's `isCurrent` is the
 * slot's resolved source, which that tag already shows, so it adds nothing here.
 *
 * @param {Forecast} forecast - The forecast.
 * @param {VolumeForecastType} forecastType - Which list it belongs to.
 * @return {DetailPart | null} The part, or null when there is nothing to say.
 */
function versionPart(
  forecast: Forecast,
  forecastType: VolumeForecastType
): DetailPart | null {
  const latest = forecastType === 'pdp_decline' && forecast.isCurrent;
  if (forecast.version !== undefined) {
    return {
      key: 'version',
      content: `Version: ${forecast.version}${latest ? ' (Latest)' : ''}`,
    };
  }
  return latest ? { key: 'version', content: 'Latest' } : null;
}

/**
 * A labelled value, e.g. "Source: ARIES".
 *
 * @param {string} key - The part's key.
 * @param {string} label - The label, without its colon.
 * @param {ReactNode} value - The value.
 * @return {DetailPart} The part.
 */
const labelled = (
  key: string,
  label: string,
  value: ReactNode
): DetailPart => ({
  key,
  content: (
    <>
      <span className={styles.detailLabel}>{label}:</span> {value}
    </>
  ),
});

/**
 * The parts present on this forecast, joined with " | " — so a missing leading part leaves
 * no stray separator at the start of the line.
 *
 * @param {object} props - Component props.
 * @param {Array<DetailPart | null | false | undefined>} props.parts - The candidate parts.
 * @return {JSX.Element} The details line.
 */
function DetailsLine({
  parts,
}: {
  parts: (DetailPart | null | false | undefined | '')[];
}) {
  const present = parts.filter((part): part is DetailPart => Boolean(part));
  return (
    <span className={styles.detailsText}>
      {present.map((part, index) => (
        <Fragment key={part.key}>
          {index > 0 && ' | '}
          {part.content}
        </Fragment>
      ))}
    </span>
  );
}

/**
 * The forecast's date. A promoted DCA forecast's date is its promotion, not its creation.
 *
 * @param {Forecast} forecast - The forecast.
 * @param {boolean} promoted - Whether the date is a promotion.
 * @return {DetailPart | null} The part, or null without a date.
 */
const datePart = (forecast: Forecast, promoted: boolean): DetailPart | null =>
  forecast.createdAt
    ? labelled(
        'date',
        promoted ? 'Promoted' : 'Created',
        <Date date={forecast.createdAt} format="MM/DD/YYYY hh:mm A" />
      )
    : null;

/**
 * Renders details for a PDP Decline forecast.
 * @param {object} props - Component props.
 * @param {PdpDeclineForecast} props.forecast - The decline.
 * @return {JSX.Element} The PDP decline details component
 */
function PdpDeclineDetails({ forecast }: { forecast: PdpDeclineForecast }) {
  return (
    <DetailsLine
      parts={[
        versionPart(forecast, 'pdp_decline'),
        datePart(forecast, Boolean(forecast.promoted)),
        forecast.forecastStatus &&
          labelled(
            'status',
            'Forecast Status',
            displayValue(forecast.forecastStatus)
          ),
        forecast.source &&
          labelled('source', 'Source', displayValue(forecast.source)),
        forecast.dcaScenario &&
          labelled('dca', 'DCA Scenario', displayValue(forecast.dcaScenario)),
      ]}
    />
  );
}

/**
 * A type curve's verdict as an icon and label (Correct / Incorrect), the way the Type Curve
 * page shows it. Nothing until a verdict is given.
 *
 * @param {TypeCurveForecast['typeCurveVerification']} verification - The curve's verdict.
 * @return {DetailPart | null} The part, or null when undecided.
 */
function verificationPart(
  verification: TypeCurveForecast['typeCurveVerification']
): DetailPart | null {
  const status = verification?.verificationStatus;
  if (!typeCurveVerificationIsDecided(status)) return null;
  return {
    key: 'verification',
    content: (
      <TypeCurveVerificationStatusIndicator
        status={status}
        showLabel
        hoverVerifiedBy
        lastVerifiedBy={verification?.lastVerifiedBy}
        className={styles.verification}
        labelClassName={styles.detailLabel}
        iconClasses={{
          correct: styles.verificationCorrect,
          incorrect: styles.verificationIncorrect,
        }}
      />
    ),
  };
}

/**
 * Renders details for a Type Curve forecast. Takes the type curve's fields with any forecast's
 * id, so the caller can hand over a list entry without narrowing it first: a decline has none
 * of the type-curve-only fields, which are all optional.
 * @param {object} props - Component props.
 * @param {TypeCurveForecast} props.forecast - The type curve.
 * @return {JSX.Element} The type curve details component
 */
function TypeCurveDetails({
  forecast,
}: {
  forecast: Omit<TypeCurveForecast, 'id'> & { id: Forecast['id'] };
}) {
  return (
    <DetailsLine
      parts={[
        versionPart(forecast, 'type_curve'),
        datePart(forecast, false),
        forecast.forecastStatus &&
          labelled(
            'status',
            'Forecast Status',
            displayValue(forecast.forecastStatus)
          ),
        forecast.spacing !== undefined &&
          labelled(
            'spacing',
            'Spacing',
            formatNumber({
              value: forecast.spacing,
              minPrecision: 0,
              maxPrecision: 0,
              includeCommas: true,
              unit: 'ft',
            })
          ),
        forecast.source &&
          labelled('source', 'Source', displayValue(forecast.source)),
        forecast.status &&
          labelled('state', 'Status', displayValue(forecast.status)),
        verificationPart(forecast.typeCurveVerification),
        forecast.archived && {
          key: 'archived',
          content: <span className={styles.detailLabel}>Archived</span>,
        },
      ]}
    />
  );
}

/**
 * Renders a forecast's details for its type.
 * @param {object} props - Component props.
 * @param {Forecast} props.forecast - The forecast.
 * @param {VolumeForecastType} props.forecastType - Which list it belongs to.
 * @return {JSX.Element} The details line
 */
export function ForecastDetails({
  forecast,
  forecastType,
}: {
  forecast: Forecast;
  forecastType: VolumeForecastType;
}) {
  return forecastType === 'pdp_decline' ? (
    <PdpDeclineDetails forecast={forecast} />
  ) : (
    <TypeCurveDetails forecast={forecast} />
  );
}

/**
 * A forecast dropdown row: the name, tagged when it is the forecast in use, with its details
 * beneath — so several fits for one well can be told apart before one is chosen.
 * @param {object} props - Component props.
 * @param {Forecast} props.forecast - The forecast.
 * @param {VolumeForecastType} props.forecastType - Which list it belongs to.
 * @param {boolean} [props.inUse] - Whether the row being edited is priced on it.
 * @return {JSX.Element} The option row
 */
export function ForecastOption({
  forecast,
  forecastType,
  inUse = false,
}: {
  forecast: Forecast;
  forecastType: VolumeForecastType;
  inUse?: boolean;
}) {
  return (
    <span className={styles.option}>
      <span className={styles.optionName}>
        {withAcronymsUpperCased(forecast.name ?? '')}
        {inUse && <InUseTag />}
      </span>
      <span className={styles.optionDetails}>
        <ForecastDetails forecast={forecast} forecastType={forecastType} />
      </span>
    </span>
  );
}
